export const MAX_SUBTYPE_DEPTH = 3;

function nodeName(item) {
  if (typeof item === "string") return String(item).trim();
  if (!item || typeof item !== "object") return "";
  return String(item.nome || item.title || item.name || "").trim();
}

function childList(item) {
  if (!item || typeof item !== "object") return [];
  if (Array.isArray(item.subtipos)) return item.subtipos;
  if (Array.isArray(item.children)) return item.children;
  return [];
}

export function emptySubtypeNode() {
  return { nome: "", subtipos: [] };
}

export function coerceSubtypeListForEditor(arr, depth = 1) {
  if (!Array.isArray(arr) || depth > MAX_SUBTYPE_DEPTH) return [];
  const mapped = arr.map((item) => {
    if (typeof item === "string") {
      return { nome: item, subtipos: [] };
    }
    return {
      nome: item && typeof item === "object" ? String(item.nome || item.title || item.name || "") : "",
      subtipos: coerceSubtypeListForEditor(childList(item), depth + 1),
    };
  });
  return mapped;
}

export function normalizeSubtypeList(arr, depth = 1) {
  if (!Array.isArray(arr) || depth > MAX_SUBTYPE_DEPTH) return [];
  return arr
    .map((item) => {
      const nome = nodeName(item);
      const subtipos = normalizeSubtypeList(childList(item), depth + 1);
      return { nome, subtipos };
    })
    .filter((n) => n.nome || (n.subtipos && n.subtipos.length));
}

export function normalizeRequestTypes(list) {
  if (!Array.isArray(list) || !list.length) return [];
  return list.map((rt) => {
    const next = coerceSubtypeListForEditor(rt?.subtipos);
    return {
      tipo: String(rt?.tipo || rt?.nome || "").trim(),
      subtipos: next.length ? next : [emptySubtypeNode()],
    };
  });
}

function isCommercialRootName(name) {
  const lower = String(name || "").toLowerCase();
  return (
    lower.includes("venda") ||
    lower.includes("comercial") ||
    lower.includes("oportunidade")
  );
}

function ensureChild(children, nome) {
  const trimmed = String(nome || "").trim();
  if (!trimmed) return null;
  const key = trimmed.toLowerCase();
  let existing = children.find((c) => String(c.nome || "").trim().toLowerCase() === key);
  if (!existing) {
    existing = { nome: trimmed, subtipos: [] };
    children.push(existing);
  }
  if (!Array.isArray(existing.subtipos)) existing.subtipos = [];
  return existing;
}

function mergeChildren(target, nodes) {
  for (const n of nodes || []) {
    const child = ensureChild(target, nodeName(n));
    if (!child) continue;
    mergeChildren(child.subtipos, childList(n));
  }
}

/**
 * Converte a resposta da IA (raiz + até 3 níveis) para o estado do wizard,
 * fundindo linhas com a mesma raiz. Raízes comerciais ficam de fora.
 */
export function aiSuggestionsToRequestTypes(suggested) {
  const raw = Array.isArray(suggested) ? suggested : [];
  const byName = new Map();
  for (const item of raw) {
    const tipo = nodeName(item);
    if (!tipo || isCommercialRootName(tipo)) continue;
    const key = tipo.toLowerCase();
    if (!byName.has(key)) byName.set(key, { tipo, subtipos: [] });
    mergeChildren(byName.get(key).subtipos, childList(item));
  }
  return Array.from(byName.values()).map((rt) => ({
    ...rt,
    subtipos: rt.subtipos.length ? rt.subtipos : [emptySubtypeNode()],
  }));
}

function formatNode(node) {
  const nome = String(node?.nome || "").trim();
  const kids = Array.isArray(node?.subtipos) ? node.subtipos.map(formatNode).filter(Boolean) : [];
  if (!nome && !kids.length) return "";
  if (!kids.length) return nome;
  return `${nome} (${kids.join("; ")})`;
}

export function formatRequestTypesSummary(requestTypes) {
  if (!Array.isArray(requestTypes) || !requestTypes.length) return "";
  return requestTypes
    .map((rt) => {
      const tipo = String(rt?.tipo || "").trim();
      if (!tipo) return null;
      const kids = normalizeSubtypeList(rt?.subtipos).map(formatNode).filter(Boolean);
      if (!kids.length) return tipo;
      return `${tipo}: ${kids.join(" · ")}`;
    })
    .filter(Boolean)
    .join(" | ");
}

function cloneRequestTypes(list) {
  return JSON.parse(JSON.stringify(Array.isArray(list) ? list : []));
}

function walkToParent(subtipos, path) {
  let list = subtipos;
  for (let i = 0; i < path.length - 1; i += 1) {
    if (!list[path[i]]) return { list: null, idx: -1 };
    if (!Array.isArray(list[path[i]].subtipos)) list[path[i]].subtipos = [];
    list = list[path[i]].subtipos;
  }
  return { list, idx: path[path.length - 1] };
}

export function setRequestTypeName(requestTypes, tipoIndex, value) {
  const next = cloneRequestTypes(requestTypes);
  if (next[tipoIndex]) next[tipoIndex].tipo = value;
  return next;
}

export function setSubtypeName(requestTypes, tipoIndex, path, value) {
  const next = cloneRequestTypes(requestTypes);
  if (!next[tipoIndex] || !path.length) return next;
  const { list, idx } = walkToParent(next[tipoIndex].subtipos, path);
  if (!list || !list[idx]) return next;
  list[idx].nome = value;
  return next;
}

export function addSubtypeChild(requestTypes, tipoIndex, path) {
  const next = cloneRequestTypes(requestTypes);
  if (!next[tipoIndex] || !path.length || path.length >= MAX_SUBTYPE_DEPTH) return next;
  const { list, idx } = walkToParent(next[tipoIndex].subtipos, path);
  if (!list || !list[idx]) return next;
  if (!Array.isArray(list[idx].subtipos)) list[idx].subtipos = [];
  list[idx].subtipos.push(emptySubtypeNode());
  return next;
}

export function addRootSubtype(requestTypes, tipoIndex) {
  const next = cloneRequestTypes(requestTypes);
  if (!next[tipoIndex]) return next;
  if (!Array.isArray(next[tipoIndex].subtipos)) next[tipoIndex].subtipos = [];
  next[tipoIndex].subtipos.push(emptySubtypeNode());
  return next;
}

export function removeSubtypeAt(requestTypes, tipoIndex, path) {
  const next = cloneRequestTypes(requestTypes);
  if (!next[tipoIndex] || !path.length) return next;
  const { list, idx } = walkToParent(next[tipoIndex].subtipos, path);
  if (!list || idx < 0) return next;
  list.splice(idx, 1);
  if (path.length === 1 && !next[tipoIndex].subtipos.length) {
    next[tipoIndex].subtipos = [emptySubtypeNode()];
  }
  return next;
}

export function addRequestType(requestTypes) {
  return [...cloneRequestTypes(requestTypes), { tipo: "", subtipos: [emptySubtypeNode()] }];
}

export function removeRequestType(requestTypes, tipoIndex) {
  const next = cloneRequestTypes(requestTypes);
  next.splice(tipoIndex, 1);
  return next;
}
